import { Link } from "react-router-dom";
import { CONTACT_EMAIL, SITE_ORIGIN } from "../config/site";
import { BtnContent } from "../components/BtnIcon";
import { IoStorefrontOutline } from "react-icons/io5";

function Shipping() {
  const lastUpdated = "July 7, 2026";

  return (
    <article className="container py-4 py-md-5" style={{ maxWidth: "720px" }}>
      <header className="mb-4">
        <h1 className="h3 mb-2">Shipping policy</h1>
        <p className="text-muted small mb-0">Last updated: {lastUpdated}</p>
      </header>

      <p className="small mb-4">
        Rencin Gifting (&ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) delivers curated
        gift boxes and hampers across India from Mumbai. This policy explains how shipping works when
        you order on{" "}
        <a href={SITE_ORIGIN}>{SITE_ORIGIN.replace(/^https?:\/\//, "")}</a>.
      </p>

      <section className="mb-4">
        <h2 className="h5 mb-2">Where we ship</h2>
        <p className="small mb-0">
          We ship to serviceable pin codes across India. At checkout, enter a valid 6-digit pin code
          to see available courier options and rates for your address. If no options appear, your
          location may not be serviceable by our shipping partners — please{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>contact us</a> and we will try to help.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Shipping rates</h2>
        <p className="small mb-2">
          Shipping cost is calculated at checkout based on your delivery pin code, cart weight, and
          collapsed packaging size (gift boxes ship flat in mailers, not at assembled size). You can
          compare available couriers and their rates before placing your order. The selected shipping
          fee is added to your order total.
        </p>
        <p className="small mb-0">
          Rates shown at checkout are estimates provided by our logistics partners. The final
          shipping charge you pay is the amount displayed and confirmed in your order summary.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Processing and dispatch</h2>
        <p className="small mb-2">
          Orders are processed after payment is confirmed. Gift boxes may require assembly,
          personalisation, or quality checks before dispatch.
        </p>
        <ul className="small ps-3 mb-0">
          <li className="mb-1">
            <strong>Standard orders</strong> — typically dispatched within 1–3 business days after
            payment confirmation, unless stated otherwise on the product page.
          </li>
          <li className="mb-1">
            <strong>Custom or personalised orders</strong> — may need additional time. We will
            contact you if your order requires a longer preparation window.
          </li>
          <li>
            <strong>Peak seasons</strong> — during festivals and holidays, dispatch may take
            longer due to higher order volume. Order early for time-sensitive gifting.
          </li>
        </ul>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Estimated delivery time</h2>
        <p className="small mb-0">
          Each courier option at checkout shows an estimated delivery window (ETD). These are
          estimates from our shipping partner and are not guaranteed delivery dates. Actual delivery
          may vary due to location, weather, courier capacity, or local holidays. Remote or
          hard-to-reach areas may take longer.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Order tracking</h2>
        <p className="small mb-0">
          Once your order is shipped, we share tracking details by email and in your order history
          when available. Shipments are handled through trusted courier partners via Shiprocket. Use
          the tracking link provided to follow your package until delivery.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Delivery attempts</h2>
        <p className="small mb-2">
          Please ensure the recipient name, phone number, and full address (including landmark and
          correct pin code) are accurate at checkout. Couriers may attempt delivery during business
          hours.
        </p>
        <p className="small mb-0">
          If delivery fails because of an incorrect address, unavailability of the recipient, or
          refusal to accept the package, re-delivery or return charges may apply. We are not
          responsible for delays caused by incomplete or incorrect delivery details.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Prepaid and cash on delivery</h2>
        <p className="small mb-2">
          You can pay online with UPI at checkout, or choose cash on delivery (COD) and pay the
          courier when your gift box arrives.
        </p>
        <p className="small mb-0">
          Online orders are processed after payment is confirmed. COD orders are processed after
          placement and must be paid in full at delivery.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Prepaid orders</h2>
        <p className="small mb-0">
          Prepaid orders are dispatched after payment is confirmed via secure UPI checkout.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Damaged or lost shipments</h2>
        <p className="small mb-2">
          Gift boxes are packed carefully for transit. If your order arrives visibly damaged or does
          not arrive within a reasonable time after the estimated delivery window, contact us within
          48 hours of the expected delivery date (or within 48 hours of receiving a damaged
          package) with your order number and photos where applicable.
        </p>
        <p className="small mb-0">
          We will review the case with our courier partner and work toward a fair resolution, which
          may include replacement or refund depending on the situation and product availability.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Address changes</h2>
        <p className="small mb-0">
          Address changes can only be made before your order is dispatched. Email us as soon as
          possible at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with your order
          number and updated delivery details. Once a shipment is handed to the courier, we cannot
          guarantee changes to the delivery address.
        </p>
      </section>

      <section className="mb-4">
        <h2 className="h5 mb-2">Contact us</h2>
        <p className="small mb-0">
          For shipping questions or help with an order in transit, email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> or visit our{" "}
          <Link to="/about">contact page</Link>.
        </p>
      </section>

      <p className="mb-0">
        <Link to="/shop" className="btn btn-primary btn-sm">
          <BtnContent icon={IoStorefrontOutline}>Back to shop</BtnContent>
        </Link>
      </p>
    </article>
  );
}

export default Shipping;
